
import { Box, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
// import { InfoGrid } from '../../utils/reportUtils';
import { convertUnderscoreToTitle } from '../../../utils/commonUtils';
import { InfoGrid, ReportFooterTimestamp } from '../../../utils/reportUtils';
import { usePatientContext } from '../../../components/SubPatinetProvider';
import DataTable from '../../../components/CycleOverViewList/CycleOverviewReport/MedicationTable';
import { CycleDetailsType, getcycleOverviewReport } from '../../../components/CycleOverViewList/CycleDetailsHelper';
import { useCycle } from '../../../components/cycleContext/CycleContext';
import { Medication,Event,Hormone,DonorSourceType } from '../../../components/CycleOverViewList/CycleOverviewReport/CycleOverviewReportHelper';
import FollicularScanDateWiseReport from './FollicularScanDateWiseReport';
import { formatDate } from '../../../utils/dateUtils';



const StimulationReport: React.FC = () => {
  const { t } = useTranslation('cycleOverview_config');
  const { t: tGeneral } = useTranslation('general');
  const { patientId } = usePatientContext();
  
  const { cycleId } = useCycle();
  const [stimulationReportData, setStimulationReportData] = React.useState<CycleDetailsType | null>(null);
  useEffect(() => {
    if (patientId) {
      getcycleOverviewReport(String(patientId), String(cycleId))
        .then((data) => {
          setStimulationReportData(data);
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Error fetching cycle overview report:', error);
          setStimulationReportData(null);
        });
    }
  }, [patientId, cycleId]);

  // Medications
  const stimulationMeds = stimulationReportData?.stimulation_chart?.medications?.filter(
    (m) => m.drug_type === 'STIMULATION'
  ) ?? [];
  const triggerMeds = stimulationReportData?.stimulation_chart?.medications?.filter(
    (m) => m.drug_type === 'TRIGGER'
  ) ?? [];

  const events = stimulationReportData?.stimulation_chart?.events ?? [];
  const hormones = stimulationReportData?.stimulation_chart?.hormones ?? [];
  const follicularStudies = stimulationReportData?.stimulation_chart?.follicular_study ?? [];   
  
  const medicationColumns = [
    { label: t('Drug Name'), render: (med: Medication) => med.drug?.name_label || '-' },
    { label: t('Molecule'), render: (med: Medication) => med.drug?.molecule_label || '-' },
    { label: t('Strength'), render: (med: Medication) => med.drug?.strength || '-' },
    { label: t('UOM'), render: (med: Medication) => med.drug?.stock_uom_label || '-' },
    { label: t('Quantity'), render: (med: Medication) => med.quantity || '-' },
    { label: t('Start Date'), render: (med: Medication) => formatDate(med.start_date) || '-' },
    { label: t('End Date'), render: (med: Medication) => formatDate(med.end_date) || '-' },
    { label: t('Start Time'), render: (med: Medication) => med.start_time || '-' },
  ];

  // Columns for events
  const eventColumns = [
    { label: t('Event Type'), render: (event: Event) => event.event_type },
    { label: t('Date'), render: (event: Event) => formatDate(event.date) || '-' },
    { label: t('Remark'), render: (event: Event) => event.appointment?.remark || '-' },
    { label: t('Status'), render: (event: Event) => event.appointment?.status || '-' },
  ];

  //coluns for hormones
  const hormoneColumns = [
    { label: t('Hormone Name'), render: (hormone: Hormone) => hormone.laboratory_test?.name || '-' },
    { label: t('Hormone Value'), render: (hormone: Hormone) => hormone.results || '-' },
    { label: t('Units'), render: (hormone: Hormone) => hormone.laboratory_test?.unit || '-' },
    { label: t('Date'), render: (hormone: Hormone) => formatDate(hormone.date) || '-' },
  ];
 
 


  const cycleOverviewGridData: string[][] = [
    // Row 1
    [
      t('CycleNumber'),
      stimulationReportData?.cycle_number || '-',
      t('CycleName'),
      stimulationReportData?.cycle_name_label || '-',
    ],

    // Row 2
    [
      convertUnderscoreToTitle(t('Indication')),
      stimulationReportData?.procedure?.indication_label || '-',
      t('OocyteSource'),
      stimulationReportData?.procedure?.donor_info?.oocyte_source?.length
        ? stimulationReportData.procedure.donor_info.oocyte_source
          .map((source: DonorSourceType) => `${source.first_name || ''} ${source.last_name || ''}`.trim())
          .join(', ')
        : '-',
    ],

    // Row 3
    [
      t('SpermSource'),
      stimulationReportData?.procedure?.donor_info?.sperm_source?.length
        ? stimulationReportData.procedure.donor_info.sperm_source
          .map((source: DonorSourceType) => `${source.first_name || ''} ${source.last_name || ''}`.trim())
          .join(', ')
        : '-',
      t('Recipient'),
      stimulationReportData?.procedure?.donor_info?.recipient
        ? `${stimulationReportData.procedure.donor_info.recipient.first_name || ''} ${stimulationReportData.procedure.donor_info.recipient.last_name || ''}`.trim()
        : '-',
    ],

    // Row 4
    [
      t('MedicationProtocol'),
      stimulationReportData?.medication_protocol_name_label || '-',
      t('CycleCreator'),
      stimulationReportData?.cycle_creator_name || '-',
    ],

    // Row 5
    [
      t('CycleCoordinator'),
      stimulationReportData?.coordinator_name || '-',
      t('CycleProvider'),
      stimulationReportData?.provider_name || '-',
    ],

    // Row 6
    [
      t('CycleStartDate'),
      formatDate(stimulationReportData?.cycle_start_date) || '-',
      t('CycleEndDate'),
      formatDate(stimulationReportData?.cycle_end_date) || '-',
    ],

    // Row 7
    [
      t('LMPDate'),
      formatDate(stimulationReportData?.lmp_date) || '-',
      t('Remark'),
      stimulationReportData?.remarks || '-',
    ],
  ];

  return (
    <>
      <Box sx={{ mb: 1, mx: 1,p:2.5, pt:0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>{t('CycleDetails')}</Typography>
        <InfoGrid data={cycleOverviewGridData} />
      </Box>
      
      <Box sx={{ mb: 1, mx: 1, p: 2.5, pt: 0 }}>
        <Typography variant="h6" sx={{ mb: 1, color: '#444' }}>{tGeneral('Stimulation_Details')}</Typography>
        {events.length > 0 && (
          <DataTable rows={events} columns={eventColumns} title="Events" />
        )}
        {stimulationMeds.length > 0 && (
          <DataTable rows={stimulationMeds} columns={medicationColumns} title="Stim Medications" />
        )}
        {triggerMeds.length > 0 && (
          <DataTable rows={triggerMeds} columns={medicationColumns} title="Trigger Medications" />
        )}
        {hormones.length > 0 && (
          <DataTable rows={hormones} columns={hormoneColumns} title="Hormones" />
        )}
        {follicularStudies.length > 0 && (
          <FollicularScanDateWiseReport follicular_studies={follicularStudies}  />
        )}
      </Box>
      <ReportFooterTimestamp />
      
    </>
  );
};

export default StimulationReport;
